import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {LIST_INITIAL_NUM_RENDERING} from 'consts'
import styled from 'styled-components'
import {ListMetrics} from 'types'
import {useDidUpdate, useElementSize, useListMasonryNotLazyAction} from 'utils'
import convertUnit from 'lib/unit'
import {FlatlistItem} from '../FlatlistItem'
import {FlatlistProps} from '../Flatlist'

interface StyledContainerProps {
  height: number
  width: number
  scrollbar?: boolean
}

interface StyledContentContainerProps {
  height: number
}

const StyledContainer = styled.div<StyledContainerProps>`
  ${({height, width}) => ({height, width})};
  display: flex;
  flex-direction: column;
  overflow: auto;
  overflow-x: hidden;

  scrollbar-width: ${({scrollbar}) => (scrollbar ? 'auto' : 'none')};
  scrollbar-color: ${({theme}) => `${theme.primary_5} ${theme.white_3}`};

  ::-webkit-scrollbar {
    background-color: ${({theme}) => theme.white_3};
    width: ${({scrollbar}) => convertUnit(scrollbar ? 25 : 0)};
  }

  ::-webkit-scrollbar-thumb {
    min-height: ${convertUnit(40)};
    background-color: ${({theme}) => theme.primary_5};
    background-clip: content-box;
    border: ${convertUnit(8)} solid ${({theme}) => theme.white_3};
    border-radius: ${convertUnit(16)};
  }
`

const StyledContentContainer = styled.div`
  width: 100%;
`

const StyledContenInnerContainer = styled.div`
  position: relative;
  width: 100%;
`

const StyledInnerContainer = styled.div<StyledContentContainerProps>`
  ${({height}) => ({height})}
  width: 100%;
`

export default function ListPaginationBase<ItemT>({
  height,
  width,
  data: dataBase,
  contentContainerStyle,
  scrollbar = true,
  listHeaderElement,
  listFooterElement,
  listEmptyElement,
  initialNumRendering = LIST_INITIAL_NUM_RENDERING,
  onScroll,
  keyExtractor,
  renderItem,
  onContentSizeChange,
  onLayoutDimension,
}: FlatlistProps<ItemT>) {
  const contentLength = useRef(0)
  const headerLength = useRef(0)
  const footerLength = useRef(0)
  const ref = useRef<HTMLDivElement | null>(null)
  const [timestamp, setTimestamp] = useState(new Date().getTime())
  const totalLength =
    headerLength.current + contentLength.current + footerLength.current

  const forceUpdate = useCallback(() => setTimestamp(new Date().getTime()), [
    setTimestamp,
  ])

  const refContent = useRef<HTMLDivElement | null>(null)
  const {clientWidth, clientHeight} = useElementSize(refContent)
  const {clientWidth: layoutWidth, clientHeight: layoutHeight} = useElementSize(
    ref,
  )
  const emptyData = useMemo(() => [], [])

  const data = useMemo(() => dataBase ?? emptyData, [dataBase, emptyData])

  const dataRelative = useMemo(() => [...data], [data])

  const minLength = useMemo(() => Math.ceil(height / initialNumRendering), [
    height,
    initialNumRendering,
  ])

  const empty = useMemo<ListMetrics>(() => ({length: 0, offset: 0}), [])

  const {
    handleGetMetrics,
    handleItemLayout,
    handleScroll,
    handleLayoutHeader,
    handleLayoutFooter,
  } = useListMasonryNotLazyAction({
    ref,
    data,
    headerLength,
    footerLength,
    contentLength,
    empty,
    minLength,
    keyExtractor,
    forceUpdate,
    onScroll,
  })

  const handleDidUpdate = useCallback(() => {
    if (ref.current) {
      forceUpdate()
    }
  }, [forceUpdate])

  const handleRenderItem = useCallback(
    (item: ItemT, relativeIndex: number, relativeArray: ItemT[]) => {
      const key = keyExtractor(item)
      const {offset} = handleGetMetrics(key)

      return (
        <FlatlistItem
          key={key}
          offset={offset + headerLength.current}
          onLayout={(length) =>
            handleItemLayout({
              key,
              relativeIndex,
              length,
              relativeArray,
            })
          }>
          {renderItem(item, relativeIndex)}
        </FlatlistItem>
      )
    },
    [keyExtractor, handleGetMetrics, renderItem, handleItemLayout],
  )

  const handleRenderHeader = useMemo(
    () => (
      <FlatlistItem onLayout={handleLayoutHeader}>
        {listHeaderElement}
      </FlatlistItem>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [timestamp, listHeaderElement, handleLayoutHeader],
  )

  const handleRenderFooter = useMemo(
    () => (
      <FlatlistItem
        offset={contentLength.current + headerLength.current}
        onLayout={handleLayoutFooter}>
        {listFooterElement}
      </FlatlistItem>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [timestamp, listFooterElement, handleLayoutFooter],
  )

  useEffect(() => {
    onLayoutDimension &&
      onLayoutDimension({width: layoutWidth, height: layoutHeight})
  }, [layoutHeight, layoutWidth, onLayoutDimension])

  useEffect(() => {
    onContentSizeChange && onContentSizeChange(clientWidth, clientHeight)
  }, [clientHeight, clientWidth, onContentSizeChange])

  const handleRenderChildren = useMemo(
    () =>
      data.length ? (
        <StyledContentContainer style={contentContainerStyle}>
          <StyledContenInnerContainer>
            <StyledInnerContainer ref={refContent} height={totalLength}>
              {handleRenderHeader}
              {dataRelative.map(handleRenderItem)}
              {handleRenderFooter}
            </StyledInnerContainer>
          </StyledContenInnerContainer>
        </StyledContentContainer>
      ) : (
        <>
          {listHeaderElement}
          {listEmptyElement}
          {listFooterElement}
        </>
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      listHeaderElement,
      listEmptyElement,
      listFooterElement,
      contentContainerStyle,
      timestamp,
      data.length,
      totalLength,
      handleRenderHeader,
      handleRenderFooter,
      handleRenderItem,
    ],
  )

  useDidUpdate(handleDidUpdate, [handleDidUpdate])

  return (
    <StyledContainer
      ref={ref}
      height={height}
      width={width}
      scrollbar={scrollbar}
      onScroll={handleScroll}>
      {handleRenderChildren}
    </StyledContainer>
  )
}
